import React, { useCallback, useEffect, useRef, useState } from "react";
import { Grid, Typography } from "@material-ui/core";
import Layout from "../../components/layout/Layout";
import CompanyAuth from "../../components/auth/CompanyAuth";
import {
  isAuth,
  getCompanyProfile,
  updateCompanyProfile,
  sendVerificationCode,
  verifyCode,
  companyResetPassword,
} from "../../actions/auth";
import { API } from "../../config";
import { generateStrongPassword } from "../../utils/passwordGenerator";

const numericOnlyFields = new Set(["pobox", "woreda", "phone", "officePhone", "fax"]);
const textOnlyFields = new Set(["city", "subCity", "state", "kebele"]);

const toSafeString = (value) => (value === undefined || value === null ? "" : String(value));

const fileNameFromPath = (value) => {
  const raw = toSafeString(value).trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    const segments = parsed.pathname.split("/");
    return segments[segments.length - 1] || "";
  } catch (err) {
    const segments = raw.replace(/\\/g, "/").split("/");
    return segments[segments.length - 1] || "";
  }
};

const resolveAssetUrl = (value) => {
  const raw = toSafeString(value).trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const apiRoot = API.replace(/\/api$/, "");
  if (raw.startsWith("images/")) return `${apiRoot}/api/${raw}`;
  if (raw.startsWith("docs/")) return `${apiRoot}/${raw}`;
  if (raw.startsWith("/")) return `${apiRoot}${raw}`;
  return raw;
};

const emptyForgot = {
  enabled: false,
  code: "",
  codeSent: false,
  verified: false,
  newPassword: "",
  confirmNewPassword: "",
  loadingAction: "",
  error: "",
  success: "",
};

const CompanyProfile = () => {
  const user = isAuth();
  const logoInputRef = useRef(null);
  const licenceInputRef = useRef(null);

  const [values, setValues] = useState({
    fetching: true,
    loading: false,
    error: "",
    success: "",
    name: user?.name || "",
    description: user?.description || "",
    email: user?.email || "",
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
    web: "",
    pobox: "",
    woreda: "",
    city: "",
    subCity: "",
    state: "",
    kebele: "",
    street: "",
    phone: "",
    officePhone: "",
    fax: "",
    logo: user?.logo || "",
    previewLogo: user?.logo ? resolveAssetUrl(user.logo) : "",
    logoFile: null,
    licence: "",
    licenceFile: null,
    licenceName: "",
  });

  const [forgot, setForgot] = useState(emptyForgot);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [generatedTarget, setGeneratedTarget] = useState("change");

  const loadProfile = useCallback(async () => {
    const data = await getCompanyProfile();
    if (data.err) {
      setValues((prev) => ({ ...prev, fetching: false, error: data.err }));
      return;
    }

    const profile = data.profile || {};
    const licencePath = toSafeString(profile.licence);
    setValues((prev) => ({
      ...prev,
      fetching: false,
      name: toSafeString(profile.name || user?.name),
      description: toSafeString(profile.description || user?.description),
      email: toSafeString(profile.email || user?.email),
      web: toSafeString(profile.web),
      pobox: toSafeString(profile.pobox),
      woreda: toSafeString(profile.woreda),
      city: toSafeString(profile.city),
      subCity: toSafeString(profile.subCity),
      state: toSafeString(profile.state),
      kebele: toSafeString(profile.kebele),
      street: toSafeString(profile.street),
      phone: toSafeString(profile.phone),
      officePhone: toSafeString(profile.officePhone),
      fax: toSafeString(profile.fax),
      logo: toSafeString(profile.logo || user?.logo),
      previewLogo: resolveAssetUrl(profile.logo || user?.logo),
      licence: licencePath,
      licenceName: fileNameFromPath(licencePath),
    }));
  }, [user?.description, user?.email, user?.logo, user?.name]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleChange = (field) => (event) => {
    let value = event.target.value;
    if (numericOnlyFields.has(field)) value = value.replace(/[^0-9]/g, "");
    if (textOnlyFields.has(field)) value = value.replace(/[^a-zA-Z\s]/g, "");
    setValues((prev) => ({ ...prev, [field]: value, error: "", success: "" }));
  };

  const handleForgotChange = (field) => (event) => {
    let value = event.target.value;
    if (field === "code") value = value.replace(/[^0-9]/g, "").slice(0, 6);
    setForgot((prev) => ({ ...prev, [field]: value, error: "", success: "" }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setValues((prev) => ({ ...prev, logoFile: file, previewLogo: preview, error: "", success: "" }));
  };

  const handleLicenceChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setValues((prev) => ({ ...prev, licenceFile: file, licenceName: file.name, error: "", success: "" }));
  };

  const toggleForgotMode = () => {
    setForgot((prev) => (prev.enabled ? { ...emptyForgot } : { ...emptyForgot, enabled: true }));
    setGeneratedPassword("");
  };

  const generatePassword = (target) => {
    setGeneratedTarget(target);
    setGeneratedPassword(generateStrongPassword(16));
  };

  const acceptGeneratedPassword = () => {
    if (!generatedPassword) return;
    if (generatedTarget === "forgot") {
      setForgot((prev) => ({
        ...prev,
        newPassword: generatedPassword,
        confirmNewPassword: generatedPassword,
      }));
    } else {
      setValues((prev) => ({
        ...prev,
        newPassword: generatedPassword,
        confirmNewPassword: generatedPassword,
      }));
    }
    setGeneratedPassword("");
  };

  const sendForgotCode = async () => {
    setForgot((prev) => ({ ...prev, loadingAction: "send", error: "", success: "" }));
    const data = await sendVerificationCode(values.email);
    if (data.err) {
      setForgot((prev) => ({ ...prev, loadingAction: "", error: data.err }));
      return;
    }
    setForgot((prev) => ({
      ...prev,
      loadingAction: "",
      codeSent: true,
      success: data.message || "Verification code sent.",
    }));
  };

  const verifyForgotCode = async () => {
    if (forgot.code.length !== 6) {
      setForgot((prev) => ({ ...prev, error: "Enter a valid 6-digit code." }));
      return;
    }
    setForgot((prev) => ({ ...prev, loadingAction: "verify", error: "", success: "" }));
    const data = await verifyCode(values.email, forgot.code);
    if (data.err) {
      setForgot((prev) => ({ ...prev, loadingAction: "", error: data.err }));
      return;
    }
    setForgot((prev) => ({
      ...prev,
      loadingAction: "",
      verified: true,
      success: "Code verified. Set your new password.",
    }));
  };

  const resetForgotPassword = async () => {
    const newPassword = forgot.newPassword.trim();
    const confirmPassword = forgot.confirmNewPassword.trim();
    if (!forgot.verified) {
      setForgot((prev) => ({ ...prev, error: "Verify the code first." }));
      return;
    }
    if (!newPassword || !confirmPassword) {
      setForgot((prev) => ({ ...prev, error: "New password and retype new password are required." }));
      return;
    }
    if (newPassword.length < 6) {
      setForgot((prev) => ({ ...prev, error: "New password must be at least 6 characters." }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setForgot((prev) => ({ ...prev, error: "New password and retype new password do not match." }));
      return;
    }

    setForgot((prev) => ({ ...prev, loadingAction: "reset", error: "", success: "" }));
    const data = await companyResetPassword({
      email: values.email,
      code: forgot.code,
      newPassword,
    });
    if (data.err) {
      setForgot((prev) => ({ ...prev, loadingAction: "", error: data.err }));
      return;
    }

    setForgot({ ...emptyForgot });
    setValues((prev) => ({
      ...prev,
      success: data.message || "Password changed successfully.",
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
      error: "",
    }));
    setGeneratedPassword("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!values.name || !values.email || !values.city || !values.phone || !values.officePhone) {
      setValues((prev) => ({
        ...prev,
        error: "Company name, email, city, personal phone, and office phone are required.",
      }));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(values.email.trim())) {
      setValues((prev) => ({
        ...prev,
        error: "Please enter a valid company email.",
      }));
      return;
    }

    const oldPassword = values.oldPassword.trim();
    const newPassword = values.newPassword.trim();
    const confirmPassword = values.confirmNewPassword.trim();
    const wantsPasswordChange = Boolean(oldPassword || newPassword || confirmPassword);

    if (wantsPasswordChange) {
      if (!oldPassword || !newPassword || !confirmPassword) {
        setValues((prev) => ({
          ...prev,
          error: "Old password, new password, and retype new password are required.",
        }));
        return;
      }
      if (newPassword.length < 6) {
        setValues((prev) => ({ ...prev, error: "New password must be at least 6 characters." }));
        return;
      }
      if (newPassword !== confirmPassword) {
        setValues((prev) => ({
          ...prev,
          error: "New password and retype new password do not match.",
        }));
        return;
      }
    }

    setValues((prev) => ({ ...prev, loading: true, error: "", success: "" }));

    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("description", values.description || "");
    formData.append("email", values.email || "");
    formData.append("web", values.web || "");
    formData.append("pobox", values.pobox || "");
    formData.append("woreda", values.woreda || "");
    formData.append("city", values.city || "");
    formData.append("subCity", values.subCity || "");
    formData.append("state", values.state || "");
    formData.append("kebele", values.kebele || "");
    formData.append("street", values.street || "");
    formData.append("phone", values.phone || "");
    formData.append("officePhone", values.officePhone || "");
    formData.append("fax", values.fax || "");
    formData.append("logo", values.logo || "");
    if (!values.licenceFile) formData.append("licence", values.licence || "");
    if (wantsPasswordChange) {
      formData.append("oldPassword", oldPassword);
      formData.append("newPassword", newPassword);
      formData.append("confirmNewPassword", confirmPassword);
    }
    if (values.logoFile) formData.append("image", values.logoFile);
    if (values.licenceFile) formData.append("licence", values.licenceFile);

    const data = await updateCompanyProfile(formData);
    if (data.err) {
      setValues((prev) => ({ ...prev, loading: false, error: data.err }));
      return;
    }

    const profile = data.profile || {};
    const refreshedLicence = toSafeString(profile.licence || values.licence);
    if (typeof window !== "undefined" && data.user) {
      const currentUser = isAuth() || {};
      localStorage.setItem("user", JSON.stringify({ ...currentUser, ...data.user }));
    }

    setValues((prev) => ({
      ...prev,
      loading: false,
      success: data.message || "Profile updated successfully.",
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
      name: toSafeString(profile.name || prev.name),
      description: toSafeString(profile.description || prev.description),
      web: toSafeString(profile.web || prev.web),
      pobox: toSafeString(profile.pobox || prev.pobox),
      woreda: toSafeString(profile.woreda || prev.woreda),
      city: toSafeString(profile.city || prev.city),
      subCity: toSafeString(profile.subCity || prev.subCity),
      state: toSafeString(profile.state || prev.state),
      kebele: toSafeString(profile.kebele || prev.kebele),
      street: toSafeString(profile.street || prev.street),
      phone: toSafeString(profile.phone || prev.phone),
      officePhone: toSafeString(profile.officePhone || prev.officePhone),
      fax: toSafeString(profile.fax || prev.fax),
      logo: toSafeString(profile.logo || prev.logo),
      previewLogo: resolveAssetUrl(profile.logo || prev.logo),
      logoFile: null,
      licence: refreshedLicence,
      licenceFile: null,
      licenceName: fileNameFromPath(refreshedLicence),
    }));
    setGeneratedPassword("");
  };

  const forgotLoading = (action) => forgot.loadingAction === action;
  const logoUrl = values.previewLogo || resolveAssetUrl(values.logo);
  const licenceUrl = values.licence ? resolveAssetUrl(values.licence) : "";

  return (
    <Layout>
      <CompanyAuth>
        <div className="card">
          <Typography variant="h6" className="page-header">
            My Profile
          </Typography>
          <div style={styles.wrapper}>
            <div style={styles.avatarSection}>
              <div style={styles.avatarWrapper} onClick={() => logoInputRef.current?.click()}>
                {logoUrl ? <img src={logoUrl} alt="Company logo" style={styles.avatar} /> : <span style={styles.avatarText}>Logo</span>}
              </div>
              <input type="file" ref={logoInputRef} accept="image/*" style={{ display: "none" }} onChange={handleLogoChange} />
              <input type="file" ref={licenceInputRef} accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleLicenceChange} />
              <p style={styles.emailText}>{values.email}</p>
            </div>

            {values.error ? <div style={styles.alert("error")}>{values.error}</div> : null}
            {values.success ? <div style={styles.alert("success")}>{values.success}</div> : null}

            {values.fetching ? (
              <div style={styles.loading}>Loading profile...</div>
            ) : (
              <form onSubmit={handleSubmit} style={styles.form}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Company Name *</label>
                    <input style={styles.input} value={values.name} onChange={handleChange("name")} required />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Your Company Email Address</label>
                    <input
                      style={styles.input}
                      value={values.email}
                      onChange={handleChange("email")}
                      type="email"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <label style={styles.label}>Description</label>
                    <textarea style={{ ...styles.input, ...styles.textarea }} value={values.description} onChange={handleChange("description")} rows={4} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Website (optional)</label>
                    <input style={styles.input} value={values.web} onChange={handleChange("web")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Personal Phone *</label>
                    <input style={styles.input} value={values.phone} onChange={handleChange("phone")} required />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Office Phone *</label>
                    <input style={styles.input} value={values.officePhone} onChange={handleChange("officePhone")} required />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Fax (optional)</label>
                    <input style={styles.input} value={values.fax} onChange={handleChange("fax")} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>City *</label>
                    <input style={styles.input} value={values.city} onChange={handleChange("city")} required />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Sub City (optional)</label>
                    <input style={styles.input} value={values.subCity} onChange={handleChange("subCity")} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>State (optional)</label>
                    <input style={styles.input} value={values.state} onChange={handleChange("state")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Kebele (optional)</label>
                    <input style={styles.input} value={values.kebele} onChange={handleChange("kebele")} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Street (optional)</label>
                    <input style={styles.input} value={values.street} onChange={handleChange("street")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>P.O. Box (optional)</label>
                    <input style={styles.input} value={values.pobox} onChange={handleChange("pobox")} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Woreda (optional)</label>
                    <input style={styles.input} value={values.woreda} onChange={handleChange("woreda")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label style={styles.label}>Business License</label>
                    <div style={styles.fileRow}>
                      <button type="button" style={styles.fileBtn} onClick={() => licenceInputRef.current?.click()}>
                        {values.licenceName ? "Change License" : "Upload License"}
                      </button>
                      <span style={styles.fileName}>{values.licenceName || "No file selected"}</span>
                    </div>
                    {licenceUrl ? <a href={licenceUrl} target="_blank" rel="noreferrer" style={styles.link}>View current license</a> : null}
                  </Grid>

                  <Grid item xs={12}>
                    <div style={styles.passwordBox}>
                      <div style={styles.passwordHeader}>
                        <strong>Change Password</strong>
                        <button type="button" onClick={toggleForgotMode} style={styles.modeBtn}>
                          {forgot.enabled ? "Use old password instead" : "Forgot password?"}
                        </button>
                      </div>

                      {!forgot.enabled ? (
                        <>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <label style={styles.label}>Retype old password</label>
                              <input type="password" style={styles.input} value={values.oldPassword} onChange={handleChange("oldPassword")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <label style={styles.label}>New password</label>
                              <input type="password" style={styles.input} value={values.newPassword} onChange={handleChange("newPassword")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <label style={styles.label}>Retype new password</label>
                              <input type="password" style={styles.input} value={values.confirmNewPassword} onChange={handleChange("confirmNewPassword")} />
                            </Grid>
                          </Grid>
                          <div style={styles.passwordTools}>
                            <button type="button" style={styles.fileBtn} onClick={() => generatePassword("change")}>Generate Password</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p style={styles.hint}>A 6-digit code will be sent to {values.email}.</p>
                          {forgot.error ? <div style={styles.smallAlert("error")}>{forgot.error}</div> : null}
                          {forgot.success ? <div style={styles.smallAlert("success")}>{forgot.success}</div> : null}
                          <div style={styles.passwordTools}>
                            <button type="button" style={styles.fileBtn} onClick={sendForgotCode} disabled={Boolean(forgot.loadingAction)}>
                              {forgotLoading("send") ? "Sending..." : forgot.codeSent ? "Resend Code" : "Send Code"}
                            </button>
                          </div>
                          {forgot.codeSent ? (
                            <>
                              <label style={styles.label}>Verification code</label>
                              <input style={styles.input} value={forgot.code} onChange={handleForgotChange("code")} maxLength={6} />
                              <div style={styles.passwordTools}>
                                <button type="button" style={styles.fileBtn} onClick={verifyForgotCode} disabled={Boolean(forgot.loadingAction) || forgot.verified}>
                                  {forgot.verified ? "Verified" : forgotLoading("verify") ? "Verifying..." : "Verify Code"}
                                </button>
                              </div>
                            </>
                          ) : null}
                          {forgot.verified ? (
                            <>
                              <label style={styles.label}>New password</label>
                              <input type="password" style={styles.input} value={forgot.newPassword} onChange={handleForgotChange("newPassword")} />
                              <label style={styles.label}>Retype new password</label>
                              <input type="password" style={styles.input} value={forgot.confirmNewPassword} onChange={handleForgotChange("confirmNewPassword")} />
                              <div style={styles.passwordTools}>
                                <button type="button" style={styles.fileBtn} onClick={() => generatePassword("forgot")}>Generate Password</button>
                                <button type="button" style={styles.fileBtn} onClick={resetForgotPassword} disabled={Boolean(forgot.loadingAction)}>
                                  {forgotLoading("reset") ? "Resetting..." : "Reset Password"}
                                </button>
                              </div>
                            </>
                          ) : null}
                        </>
                      )}

                      {generatedPassword ? (
                        <div style={styles.generated}>
                          <div>{generatedPassword}</div>
                          <div style={styles.passwordTools}>
                            <button type="button" style={styles.fileBtn} onClick={acceptGeneratedPassword}>Accept</button>
                            <button type="button" style={styles.fileBtn} onClick={() => setGeneratedPassword("")}>Reject</button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <button type="submit" style={values.loading ? { ...styles.saveBtn, ...styles.disabledBtn } : styles.saveBtn} disabled={values.loading}>
                      {values.loading ? "Saving..." : "Save Changes"}
                    </button>
                  </Grid>
                </Grid>
              </form>
            )}
          </div>
        </div>
      </CompanyAuth>
    </Layout>
  );
};

const styles = {
  wrapper: {
    maxWidth: 980,
    margin: "0 auto",
    padding: "1rem",
  },
  avatarSection: {
    textAlign: "center",
    marginBottom: "1rem",
  },
  avatarWrapper: {
    width: 100,
    height: 100,
    borderRadius: "50%",
    margin: "0 auto",
    overflow: "hidden",
    cursor: "pointer",
    background: "#e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatar: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  avatarText: { color: "#4a5568", fontWeight: 700 },
  emailText: { color: "#718096", marginTop: "0.5rem" },
  form: {
    background: "#fff",
    borderRadius: 12,
    padding: "1rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  },
  label: {
    display: "block",
    marginBottom: "0.35rem",
    fontWeight: 600,
    fontSize: "0.86rem",
    color: "#2d3748",
  },
  input: {
    width: "100%",
    border: "1px solid #cbd5e0",
    borderRadius: 8,
    padding: "0.7rem 0.8rem",
    boxSizing: "border-box",
    fontSize: "0.9rem",
  },
  textarea: {
    resize: "vertical",
    minHeight: 90,
  },
  disabled: {
    background: "#f7fafc",
    color: "#a0aec0",
  },
  loading: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "0.85rem",
  },
  alert: (type) => ({
    marginBottom: "0.8rem",
    borderRadius: 8,
    padding: "0.75rem",
    color: "#fff",
    fontWeight: 600,
    background: type === "error" ? "#ef4444" : "#22c55e",
  }),
  passwordBox: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "0.9rem",
    background: "#f8fafc",
  },
  passwordHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.6rem",
    marginBottom: "0.8rem",
  },
  modeBtn: {
    border: "none",
    background: "transparent",
    color: "#667eea",
    fontWeight: 700,
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
  },
  hint: {
    margin: "0 0 0.5rem",
    color: "#4a5568",
  },
  passwordTools: {
    display: "flex",
    gap: "0.6rem",
    marginTop: "0.6rem",
    flexWrap: "wrap",
  },
  smallAlert: (type) => ({
    marginTop: "0.35rem",
    borderRadius: 8,
    padding: "0.5rem 0.6rem",
    color: "#fff",
    fontSize: "0.82rem",
    background: type === "error" ? "#ef4444" : "#16a34a",
  }),
  generated: {
    marginTop: "0.7rem",
    border: "1px solid #93c5fd",
    background: "#dbeafe",
    borderRadius: 8,
    padding: "0.7rem",
    wordBreak: "break-all",
    fontSize: "0.85rem",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    flexWrap: "wrap",
  },
  fileBtn: {
    border: "none",
    borderRadius: 8,
    padding: "0.55rem 0.8rem",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.82rem",
  },
  fileName: { color: "#4a5568", fontSize: "0.82rem" },
  link: {
    display: "inline-block",
    marginTop: "0.45rem",
    color: "#667eea",
    fontSize: "0.82rem",
    textDecoration: "none",
    fontWeight: 600,
  },
  saveBtn: {
    width: "100%",
    border: "none",
    borderRadius: 10,
    padding: "0.85rem",
    background: "linear-gradient(135deg, rgba(244,151,3,0.92), rgba(20,61,89,0.95))",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  disabledBtn: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
};

export default CompanyProfile;
